import {
  Box,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  TextField,
  CircularProgress,
} from '@mui/material'
import { Button } from '@ds/components'
import { useState } from 'react'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { type Control, type FieldErrors, Controller } from 'react-hook-form'
import { SHARE_LOGIN_FORM_STRINGS } from './strings'

type TShareLoginFormData = {
  password: string
}

type TShareLoginFormProps = {
  control: Control<TShareLoginFormData>
  errors: FieldErrors<TShareLoginFormData>
  isValid: boolean
  isLoading: boolean
  onSubmit: (e: React.FormEvent) => void
}

export const ShareLoginForm = ({
  control,
  errors,
  isValid,
  isLoading,
  onSubmit,
}: TShareLoginFormProps) => {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pt: { xs: 12, sm: 8, md: 6 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4, md: 5 },
          minWidth: { xs: '90%', sm: 400, md: 480 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 500 }}>
          {SHARE_LOGIN_FORM_STRINGS.title}
        </Typography>
        <Box component="form" sx={{ width: '100%' }} onSubmit={onSubmit}>
          <Controller
            name="password"
            control={control}
            rules={{
              required: SHARE_LOGIN_FORM_STRINGS.passwordRequired,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={SHARE_LOGIN_FORM_STRINGS.passwordLabel}
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                sx={{
                  mb: 3,
                  '& .MuiFormHelperText-root': {
                    minHeight: '20px',
                    margin: '4px 0 0 0',
                  },
                }}
                autoComplete="current-password"
                error={!!errors.password}
                helperText={errors.password?.message || ' '}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(show => !show)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ px: 3 }}
              disabled={isLoading || !isValid}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isLoading
                ? SHARE_LOGIN_FORM_STRINGS.loggingInButton
                : SHARE_LOGIN_FORM_STRINGS.loginButton}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
